import React, { useState, useMemo, useEffect } from "react";
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "./ui.scss";

// --- Helpers ---------------------------------------------------------------
export const Truncate = ({ text, fallback = "—" }) => {
  const value =
    text === null || text === undefined || text === "" ? null : String(text);
  return value ? (
    <span className="hr-truncate" title={value}>
      {value}
    </span>
  ) : (
    <span className="hr-muted">{fallback}</span>
  );
};

const isBlank = (v) => v === null || v === undefined || v === "";

const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const getPageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push("gap-start");
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < total - 1) items.push("gap-end");
  items.push(total);
  return items;
};

// --- Pagination ------------------------------------------------------------
export const Pagination = ({ page, totalPages, onChange }) => (
  <nav className="hr-pager" aria-label="Pagination">
    <button
      type="button"
      className="hr-pager__btn"
      onClick={() => onChange(page - 1)}
      disabled={page <= 1}
      aria-label="Previous page"
    >
      <ChevronLeft size={16} aria-hidden="true" />
    </button>

    {getPageItems(page, totalPages).map((item) =>
      typeof item === "number" ? (
        <button
          key={item}
          type="button"
          className={`hr-pager__btn ${item === page ? "is-current" : ""}`}
          aria-label={`Page ${item}`}
          aria-current={item === page ? "page" : undefined}
          onClick={() => onChange(item)}
        >
          {item}
        </button>
      ) : (
        <span key={item} className="hr-pager__gap" aria-hidden="true">
          …
        </span>
      ),
    )}

    <button
      type="button"
      className="hr-pager__btn"
      onClick={() => onChange(page + 1)}
      disabled={page >= totalPages}
      aria-label="Next page"
    >
      <ChevronRight size={16} aria-hidden="true" />
    </button>
  </nav>
);

// --- Data table ------------------------------------------------------------
/**
 * columns: [{
 *   key, header,
 *   sortValue?: (row) => string | number | null   // presence makes it sortable
 *   render: (row) => ReactNode,
 *   align?: "left" | "center" | "right",
 *   interactive?: boolean   // clicks inside the cell don't trigger onRowClick
 * }]
 *
 * Sorting and pagination are client-side (the leave API returns the full
 * list). `resetKey` returns the table to page 1 whenever the filters change.
 */
const DataTable = ({
  title,
  columns,
  rows,
  rowKey,
  loading = false,
  emptyState,
  onRowClick,
  pageSizeOptions = [10, 25, 50],
  defaultPageSize = 10,
  resetKey,
  toolbarExtra,
}) => {
  const [sort, setSort] = useState({ key: null, dir: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column || !column.sortValue) return rows;
    const direction = sort.dir === "desc" ? -1 : 1;

    return rows
      .map((row, index) => ({ row, index, value: column.sortValue(row) }))
      .sort((a, b) => {
        const aBlank = isBlank(a.value);
        const bBlank = isBlank(b.value);
        if (aBlank && bBlank) return a.index - b.index;
        if (aBlank) return 1; // blanks always last, whatever the direction
        if (bBlank) return -1;
        const result = compareValues(a.value, b.value) * direction;
        return result !== 0 ? result : a.index - b.index;
      })
      .map((entry) => entry.row);
  }, [rows, columns, sort]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const pageRows = sortedRows.slice(startIndex, endIndex);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  };

  const handleRowKeyDown = (e, row) => {
    if (e.target !== e.currentTarget) return; // ignore keys from inner controls
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onRowClick(row);
    }
  };

  const showEmpty = !loading && total === 0;

  return (
    <section className="hr-card hr-table-card" aria-label={title}>
      {title && (
        <header className="hr-table-card__header">
          <h2>{title}</h2>
        </header>
      )}

      <div className="hr-table-toolbar">
        <span className="hr-table-count" aria-live="polite">
          {loading
            ? "Loading…"
            : `${total} ${total === 1 ? "record" : "records"}`}
        </span>
        <div className="hr-table-toolbar__right">
          {toolbarExtra}
          <label className="hr-pagesize">
            <span>Rows per page</span>
            <select
              className="hr-select__control hr-select__control--sm"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {showEmpty ? (
        emptyState
      ) : (
        <div
          className="hr-table-scroll"
          tabIndex={0}
          role="region"
          aria-label={`${title || "Data"} table`}
        >
          <table className="hr-table">
            <thead>
              <tr>
                {columns.map((column) => {
                  const sortable = Boolean(column.sortValue);
                  const active = sort.key === column.key;
                  const ariaSort = active
                    ? sort.dir === "desc"
                      ? "descending"
                      : "ascending"
                    : "none";
                  const SortIcon = !active
                    ? ChevronsUpDown
                    : sort.dir === "desc"
                      ? ChevronDown
                      : ChevronUp;

                  return (
                    <th
                      key={column.key}
                      scope="col"
                      className={
                        column.align ? `is-${column.align}` : undefined
                      }
                      aria-sort={sortable ? ariaSort : undefined}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          className={`hr-th-btn ${active ? "is-sorted" : ""}`}
                          onClick={() => toggleSort(column.key)}
                        >
                          {column.header}
                          <SortIcon size={14} aria-hidden="true" />
                        </button>
                      ) : (
                        column.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }, (_, rowIndex) => (
                    <tr key={`sk-${rowIndex}`}>
                      {columns.map((column) => (
                        <td key={column.key}>
                          <span className="hr-skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                : pageRows.map((row, index) => (
                    <tr
                      key={rowKey(row, startIndex + index)}
                      className={onRowClick ? "is-clickable" : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      onKeyDown={
                        onRowClick ? (e) => handleRowKeyDown(e, row) : undefined
                      }
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={
                            column.align ? `is-${column.align}` : undefined
                          }
                          onClick={
                            column.interactive
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {column.render(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && (
        <div className="hr-table-footer">
          <span>
            Showing {startIndex + 1}–{endIndex} of {total}{" "}
            {total === 1 ? "record" : "records"}
          </span>
          {totalPages > 1 && (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onChange={setPage}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default DataTable;
